import type { QueryClient } from '@tanstack/react-query'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	type ErrorComponentProps,
	Outlet,
	redirect,
	useRouter
} from '@tanstack/react-router'
import { AccountHeader } from '@/components/portal-shell'
import { useTranslation } from '@/lib/i18n'
import {
	portalMeQueryOptions,
	portalSessionQueryOptions
} from '@/lib/portal-session'
import { isPortalSessionAuthorized } from '@/lib/portal-session-state'

export const Route = createFileRoute('/(private)')({
	ssr: false,
	beforeLoad: ({ context, location }) =>
		requirePortalAuthentication(context.queryClient, location.href),
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(portalMeQueryOptions()),
	component: PrivatePortalLayout,
	errorComponent: PrivatePortalError
})

export async function requirePortalAuthentication(
	queryClient: QueryClient,
	redirectTarget: string
) {
	const session = await queryClient.fetchQuery(portalSessionQueryOptions())
	if (isPortalSessionAuthorized(session)) {
		return
	}

	throw redirect({
		to: '/login',
		search: { redirect: redirectTarget }
	})
}

function PrivatePortalLayout() {
	const user = useSuspenseQuery(portalMeQueryOptions()).data
	return (
		<div className='portal-page'>
			<AccountHeader user={user} />
			<Outlet />
		</div>
	)
}

function PrivatePortalError(props: ErrorComponentProps) {
	const router = useRouter()
	const { t } = useTranslation()

	return (
		<div className='portal-page'>
			<AccountHeader />
			<main className='portal-container dashboard-main'>
				<section
					className='portal-card dashboard-route-error'
					role='alert'
				>
					<h1>{t('common:private_area.error_title')}</h1>
					<p>{t('common:private_area.error_description')}</p>
					<button
						className='portal-button portal-button--secondary'
						onClick={async () => {
							props.reset()
							await router.invalidate()
						}}
						type='button'
					>
						{t('common:dashboard.retry')}
					</button>
				</section>
			</main>
		</div>
	)
}
